import React from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Clock, Calendar, ShieldAlert, X } from 'lucide-react';

export interface JanelaModalData {
  isOpen: boolean;
  tipo: 'expirou' | 'antecedencia' | 'data_diferente' | 'erro';
  aula?: {
    materia?: { nome?: string };
    turma?: { nome?: string };
    numeroAula?: number;
    horaInicio?: string | null;
    horaFim?: string | null;
  } | null;
  turmaNome?: string;
  dataReferencia?: string;
  horaAtual?: string;
  limiteFormatado?: string;
  toleranciaMin?: number;
  janelaExtraMin?: number;
  mensagem: string;
  onClose: () => void;
  onProceed?: () => void;
  proceedLabel?: string;
}

export function ChamadaJanelaModal({
  isOpen,
  tipo,
  aula,
  turmaNome,
  dataReferencia,
  horaAtual,
  limiteFormatado,
  toleranciaMin = 10,
  janelaExtraMin = 60,
  mensagem,
  onClose,
  onProceed,
  proceedLabel = 'Fazer Chamada com Justificativa',
}: JanelaModalData) {
  if (!isOpen) return null;

  const materiaNome = aula?.materia?.nome || (aula?.numeroAula ? `${aula.numeroAula}ª Aula` : 'Aula');
  const turmaFinal = turmaNome || aula?.turma?.nome || 'Turma';

  const dataFormatada = dataReferencia
    ? new Date(dataReferencia + 'T12:00:00').toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : 'Data de hoje';

  const isExpirou = tipo === 'expirou';
  const isAntecedencia = tipo === 'antecedencia';
  const isDataDiferente = tipo === 'data_diferente';

  const modalTitle = isDataDiferente
    ? 'Chamada em Data Diferente (Fora do Horário)'
    : isAntecedencia
    ? 'Aguardando Início da Aula'
    : isExpirou
    ? 'Chamada Fora do Horário da Grade'
    : 'Limite de Horário da Chamada';

  const iconColor = isAntecedencia ? '#2563eb' : '#d97706';
  const iconBg = isAntecedencia ? '#eff6ff' : '#fffbeb';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="janela-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100dvh',
        zIndex: 99999,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        boxSizing: 'border-box',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          background: '#ffffff',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'fadeIn 0.2s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 20px 14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            borderBottom: '1px solid #f1f5f9',
            background: '#ffffff',
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: iconBg,
              color: iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isDataDiferente ? (
              <Calendar size={22} />
            ) : isAntecedencia ? (
              <Clock size={22} />
            ) : (
              <ShieldAlert size={22} />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2
                id="janela-modal-title"
                style={{
                  margin: 0,
                  fontSize: '1.05rem',
                  fontWeight: 800,
                  color: '#0f172a',
                  lineHeight: 1.3,
                }}
              >
                {modalTitle}
              </h2>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
              Auditoria de horário e controle de conformidade pedagógica
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              border: 'none',
              background: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Corpo do Modal */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Card Resumo da Aula */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Dados da Aula
              </span>
              {aula?.horaInicio && aula?.horaFim && (
                <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#2563eb', background: '#eff6ff', padding: '2px 8px', borderRadius: 6 }}>
                  {aula.horaInicio} – {aula.horaFim}
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#0f172a' }}>
              {materiaNome} · <span style={{ color: '#475569', fontWeight: 600 }}>{turmaFinal}</span>
            </div>
            <div style={{ fontSize: '0.76rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={13} style={{ color: '#94a3b8' }} />
              {dataFormatada}
            </div>
          </div>

          {/* Grid de Parâmetros de Horário */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 10,
            }}
          >
            <div
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 12px',
              }}
            >
              <span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Horário Atual Detectado
              </span>
              <span style={{ display: 'block', fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                {horaAtual || '--:--'}
              </span>
              <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                Fuso: Horário de Brasília
              </span>
            </div>

            <div
              style={{
                background: isExpirou ? '#fffbeb' : '#f8fafc',
                border: `1px solid ${isExpirou ? '#fde68a' : '#e2e8f0'}`,
                borderRadius: 10,
                padding: '10px 12px',
              }}
            >
              <span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: isExpirou ? '#b45309' : '#64748b', textTransform: 'uppercase' }}>
                Limite da Grade Regular
              </span>
              <span style={{ display: 'block', fontSize: '1.05rem', fontWeight: 800, color: isExpirou ? '#d97706' : '#0f172a', marginTop: 2 }}>
                {limiteFormatado || aula?.horaFim || '--:--'}
              </span>
              <span style={{ fontSize: '0.66rem', color: isExpirou ? '#b45309' : '#94a3b8' }}>
                +{toleranciaMin}min tol. +{janelaExtraMin}min extra
              </span>
            </div>
          </div>

          {/* Mensagem / Explicação Pedagógica */}
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 8,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              fontSize: '0.8rem',
              color: '#92400e',
              lineHeight: 1.45,
            }}
          >
            <strong style={{ color: '#78350f' }}>{mensagem}</strong>
            <p style={{ margin: '6px 0 0', fontSize: '0.74rem', color: '#92400e', opacity: 0.95 }}>
              {isExpirou || isDataDiferente
                ? 'Conforme as diretrizes pedagógicas, você pode prosseguir normalmente com a chamada de aluno por aluno. A chamada será salva com justificativa e passará por homologação da Coordenação.'
                : 'A lista de presença será liberada automaticamente no início da aula ou com até 5 minutos de antecedência.'}
            </p>
          </div>
        </div>

        {/* Rodapé / Ações */}
        <div
          style={{
            padding: '12px 20px',
            background: '#f8fafc',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#475569',
              fontSize: '0.82rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {onProceed ? 'Cancelar' : 'Entendido'}
          </button>
          {onProceed && (
            <button
              type="button"
              onClick={onProceed}
              style={{
                padding: '9px 18px',
                borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #d97706, #b45309)',
                color: '#ffffff',
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(217, 119, 6, 0.3)',
              }}
            >
              {proceedLabel}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
